import {Canvg} from "canvg";
import {GEAR_IDS} from "../Components/GearsScoreboard";

export type ShareOutcome = "shared" | "cancelled" | "copied" | "unavailable";

export interface ShareResultsOptions {
    /** The gears result SVG element to render into the share image. */
    svg: SVGSVGElement;
    /** Offscreen canvas used to rasterize the SVG. */
    canvas: HTMLCanvasElement;
    scoreBoard: boolean[];
    score: number;
    total: number;
    lapsToday: number;
    gameUrl: string;
    /** Hidden results markup copied to the clipboard when Web Share is unavailable. */
    hiddenResultsEl: Element | null;
    /** Preview <img> that receives the rendered share image, if present. */
    previewImg: HTMLImageElement | null;
    canUseClipboard: boolean;
}

/** True when either share path (Web Share API or clipboard) can be offered. */
export function canShareResults(canUseClipboard: boolean): boolean {
    return canUseClipboard || Boolean(navigator.canShare && navigator.canShare({text: "message"}));
}

async function renderScoreImage(options: ShareResultsOptions): Promise<Blob> {
    const {svg, canvas, scoreBoard, previewImg} = options;
    let svgString = new XMLSerializer().serializeToString(svg);
    scoreBoard.forEach((value, index) => {
        const gearId = GEAR_IDS[index];
        svgString = svgString.replace(
            `id="${gearId}" fill="#ddd"`,
            `id="${gearId}" fill="${value ? "lawngreen" : "red"}"`);
    });

    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Canvas 2D context unavailable");
    }
    const renderer = Canvg.fromString(context, svgString);
    await renderer.render();

    const dataUrl = canvas.toDataURL();
    if (previewImg) {
        previewImg.src = dataUrl;
    }
    return (await fetch(dataUrl)).blob();
}

/**
 * Shares the day's result: Web Share API with the rendered score image when
 * supported, otherwise copies the hidden results markup to the clipboard.
 */
export async function shareResults(options: ShareResultsOptions): Promise<ShareOutcome> {
    const {score, total, lapsToday, gameUrl, hiddenResultsEl, canUseClipboard} = options;
    const blob = await renderScoreImage(options);
    const shareMessage = `gEAR'D!! ${score}/${total} Lap #${lapsToday}\nPlay gEAR'D!! today @ ${gameUrl}\n`;

    if (navigator.canShare && navigator.canShare({text: "message"})) {
        const files = [new File([blob], "geared.png", {type: blob.type, lastModified: Date.now()})];
        try {
            await navigator.share({text: shareMessage, files});
            return "shared";
        } catch (e) {
            return String(e).includes("Abort") ? "cancelled" : "unavailable";
        }
    }

    if (canUseClipboard && hiddenResultsEl) {
        await navigator.clipboard.write([
            new ClipboardItem({
                "text/html": new Blob([hiddenResultsEl.innerHTML], {type: "text/html"}),
            }),
        ]);
        return "copied";
    }

    return "unavailable";
}
